import React, { useState, useEffect } from "react";
import { Form, Button, Table, Modal } from "react-bootstrap";
import { Icon } from "@iconify/react";
import { useButtonTableStyle } from "../utils/useButtonTableStyle";
import { formatIndianRupee } from "../utils/formatLiveRate";
import toast from "react-hot-toast";
import { searchProductTrail } from "../erpClient";

function HuidBarcodeSearch() {
  const { buttonClass, cardClass, tableClass } = useButtonTableStyle();
  const [searchType, setSearchType] = useState("HUID");
  const [searchValue, setSearchValue] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [searchResults, setSearchResults] = useState(null);
  const [activeTab, setActiveTab] = useState("product");
  const [showImageModal, setShowImageModal] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const [showChargesModal, setShowChargesModal] = useState(false);
  const imageBaseUrl = (process.env.REACT_APP_IMAGE_BASE_URL || "").replace(
    /\/+$/,
    "",
  );
  const getImageUrl = (imagePath) => {
    if (!imagePath) return "";
    if (/^https?:\/\//i.test(String(imagePath))) return String(imagePath);
    const clean = String(imagePath).replace(/^\/+/, "");
    if (!imageBaseUrl) return clean;
    return `${imageBaseUrl}/${clean}`;
  };

  const normalizeTrailCustomer = (source) => {
    if (!source || typeof source !== "object") return null;

    const name =
      source.name ||
      source.customer_name ||
      source.fullName ||
      source.full_name ||
      "";
    const mobile =
      source.mobile ||
      source.phone ||
      source.customer_mobile ||
      "";
    const city = source.city || source.customer_city || "";
    const state = source.state || source.customer_state || "";

    if (!name && !mobile && !city && !state) return null;

    return {
      name: name || "N/A",
      mobile: mobile || "N/A",
      city: city || "N/A",
      state: state || "N/A",
    };
  };

  const getTrailCustomer = (trail) => {
    if (!trail) return null;

    const nestedSources = [
      trail.user,
      trail.customer,
      trail.customer_details,
      trail.user_details,
      trail.sale_details?.user,
      trail.sale_details?.customer,
      trail.sale_details?.customer_details,
      trail.sales?.user,
      trail.sales?.customer,
      trail.sales?.customer_details,
      Array.isArray(trail.sale_details?.userss)
        ? trail.sale_details.userss[0]
        : trail.sale_details?.userss,
      Array.isArray(trail.sales?.userss) ? trail.sales.userss[0] : trail.sales?.userss,
    ];

    for (const source of nestedSources) {
      const customer = normalizeTrailCustomer(source);
      if (customer) return customer;
    }

    return (
      normalizeTrailCustomer(trail.sale_details) ||
      normalizeTrailCustomer(trail.sales)
    );
  };

  const getTrailSaleNumber = (trail) => {
    if (!trail) return null;

    const sources = [trail.sale_details, trail.sales, trail.sale].filter(Boolean);
    for (const source of sources) {
      const saleNumber =
        source.voucher_number ??
        source.doc_number ??
        source.invoice_no ??
        source.sale_no ??
        source.sale_number ??
        source.bill_no;
      if (saleNumber != null && String(saleNumber).trim() !== "") {
        return String(saleNumber);
      }
    }

    return null;
  };

  // Listen for API URL changes and clear search results
  useEffect(() => {
    const handleApiUrlChange = (event) => {
      console.log(
        "API URL changed in HuidBarcodeSearch, clearing results...",
        event.detail,
      );
      setSearchResults(null);
      setSearchValue("");
      toast("Database switched. Please search again.");
    };

    window.addEventListener("apiUrlChanged", handleApiUrlChange);

    return () => {
      window.removeEventListener("apiUrlChanged", handleApiUrlChange);
    };
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!searchValue.trim()) {
      toast.error("Please enter a value to search");
      return;
    }

    setIsLoading(true);
    try {
      // Convert "Barcode No" to "barcode_no" for API, keep "HUID" as "huid"
      const apiType = searchType === "Barcode No" ? "barcode_no" : "huid";

      const response = await searchProductTrail({
        type: apiType,
        id: searchValue.trim(),
      });

      // console.log("Product trail API response:", response.data);

      const isSuccess =
        response?.success === true ||
        String(response?.status || "").toLowerCase() === "success";
      const productTrail = response?.data?.product_trail || null;

      if (isSuccess && productTrail) {
        setSearchResults(productTrail);
        toast.success("Search completed successfully");
      } else {
        setSearchResults(null);
        toast.error(response?.message || "No results found");
      }
    } catch (error) {
      console.error("Error searching product trail:", error);
      setSearchResults(null);
      toast.error(
        error.response?.data?.message ||
          error.message ||
          "Failed to search. Please try again.",
      );
    } finally {
      setIsLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-IN");
  };

  const formatCurrency = (amount) => formatIndianRupee(amount);

  const trailCustomer = searchResults ? getTrailCustomer(searchResults) : null;
  const trailSaleNumber = searchResults
    ? getTrailSaleNumber(searchResults)
    : null;

  return (
    <>
      <div className="body-wrapper">
        <div className="container-fluid">
          <div
            className={`card w-100 position-relative overflow-hidden ${cardClass}`}
          >
            <div className="px-4 py-3 border-bottom">
              <h4 className="card-title mb-0">HUID / Barcode Search</h4>
            </div>

            <div className="card-body p-4">
              <Form onSubmit={handleSubmit}>
                <div className={`rounded-4 border p-4 mb-4 ${cardClass}`}>
                  <div className="row align-items-end g-3">
                    <div className="col-lg-3 col-md-4">
                      <Form.Label className="fw-semibold mb-2 text-dark">
                        Search type
                      </Form.Label>
                      <div className="d-flex gap-3 flex-wrap">
                        <Form.Check
                          type="radio"
                          id="search-type-huid"
                          name="searchType"
                          label="HUID"
                          value="HUID"
                          checked={searchType === "HUID"}
                          onChange={(e) => setSearchType(e.target.value)}
                          className={buttonClass}
                        />
                        <Form.Check
                          type="radio"
                          id="search-type-barcode"
                          name="searchType"
                          label="Barcode No"
                          value="Barcode No"
                          checked={searchType === "Barcode No"}
                          onChange={(e) => setSearchType(e.target.value)}
                          className={buttonClass}
                        />
                      </div>
                    </div>

                    <div className="col-lg-6 col-md-5">
                      <Form.Label
                        className="fw-semibold mb-2 text-dark"
                        htmlFor="search-value"
                      >
                        Enter {searchType}
                      </Form.Label>
                      <Form.Control
                        id="search-value"
                        type="text"
                        placeholder={`Enter ${searchType} value`}
                        value={searchValue}
                        onChange={(e) => setSearchValue(e.target.value)}
                        className={buttonClass}
                      />
                    </div>

                    <div className="col-lg-3 col-md-3 d-grid">
                      <Button
                        type="submit"
                        variant="primary"
                        className={buttonClass}
                        disabled={isLoading}
                      >
                        {isLoading ? "Searching..." : "Submit"}
                      </Button>
                    </div>
                  </div>
                </div>
              </Form>

              {/* Search Results */}
              {searchResults && (
                <>
                  <div className="d-flex justify-content-center mt-4 mb-4">
                    <div className="d-inline-flex overflow-hidden rounded-pill border bg-white shadow-sm">
                      {[
                        ["product", "Product"],
                        ["supplier", "Supplier"],
                        ["sale", "Sale"],
                        ["payments", "Payments"],
                      ].map(([key, label]) => (
                        <button
                          key={key}
                          type="button"
                          className={`btn border-0 rounded-0 px-4 py-2 ${activeTab === key ? "btn-primary text-white" : "bg-white text-dark"} ${buttonClass}`}
                          style={{ minWidth: "110px", boxShadow: "none" }}
                          onClick={() => setActiveTab(key)}
                        >
                          {label}
                        </button>
                      ))}
                    </div>
                  </div>

                  {activeTab === "product" && (
                    <div className="pt-4">
                      <div className="row g-3">
                        <div className="col-md-6 col-lg-4">
                          <div
                            className={`h-100 rounded-4 border p-3 text-dark ${cardClass}`}
                          >
                            <div className="d-flex justify-content-between align-items-center mb-3">
                              <span className="text-dark small">Tag No</span>
                              <span className="fw-semibold">
                                {searchResults.product?.tag_no || "N/A"}
                              </span>
                            </div>
                            <div className="d-flex justify-content-between align-items-center mb-3">
                              <span className="text-dark small">HUID</span>
                              <span className="fw-semibold">
                                {searchResults.product?.huid || "N/A"}
                              </span>
                            </div>
                            <div className="d-flex justify-content-between align-items-center mb-3">
                              <span className="text-dark small">Design</span>
                              <span className="fw-semibold">
                                {searchResults.product?.design || "N/A"}
                              </span>
                            </div>
                            <div className="d-flex justify-content-between align-items-center mb-3">
                              <span className="text-dark small">Size</span>
                              <span className="fw-semibold">
                                {searchResults.product?.size || "N/A"}
                              </span>
                            </div>
                            <div className="d-flex justify-content-between align-items-center mb-3">
                              <span className="text-dark small">Pcs</span>
                              <span className="fw-semibold">
                                {searchResults.product?.pcs || 0}
                              </span>
                            </div>
                            <div className="d-flex justify-content-between align-items-center">
                              <span className="text-dark small">
                                Gross Weight
                              </span>
                              <span className="fw-semibold">
                                {searchResults.product?.gwt ??
                                  searchResults.product?.GWT ??
                                  "0.00"}{" "}
                                g
                              </span>
                            </div>
                          </div>
                        </div>
                        <div className="col-md-6 col-lg-4">
                          <div
                            className={`h-100 rounded-4 border p-3 text-dark ${cardClass}`}
                          >
                            <div className="d-flex justify-content-between align-items-center mb-3">
                              <span className="text-dark small">
                                Less Wt (Vepari)
                              </span>
                              <span className="fw-semibold">
                                {searchResults.product?.lwt_vepari ??
                                  searchResults.product?.LWt_vepari ??
                                  "0.00"}{" "}
                                g
                              </span>
                            </div>
                            <div className="d-flex justify-content-between align-items-center mb-3">
                              <span className="text-dark small">
                                Less Wt (Customer)
                              </span>
                              <span className="fw-semibold">
                                {searchResults.product?.lwt_customer ??
                                  searchResults.product?.LWt_customer ??
                                  "0.00"}{" "}
                                g
                              </span>
                            </div>
                            <div className="d-flex justify-content-between align-items-center mb-3">
                              <span className="text-dark small">
                                Net Weight
                              </span>
                              <span className="fw-semibold">
                                {searchResults.product?.nwt ??
                                  searchResults.product?.NWT ??
                                  "0.00"}{" "}
                                g
                              </span>
                            </div>
                            <div className="d-flex justify-content-between align-items-center mb-3">
                              <span className="text-dark small">Labour %</span>
                              <span className="fw-semibold">
                                {searchResults.product?.lbr_percent ??
                                  searchResults.product?.LBR ??
                                  "0.00"}
                                %
                              </span>
                            </div>
                            <div className="d-flex justify-content-between align-items-center mb-3">
                              <span className="text-dark small">
                                Labour Type
                              </span>
                              <span className="fw-semibold">
                                {searchResults.product?.lbr_type ??
                                  searchResults.product?.LBR_type ??
                                  "N/A"}
                              </span>
                            </div>
                            <div className="d-flex justify-content-between align-items-center mb-3">
                              <span className="text-dark small">
                                Labour Amount
                              </span>
                              <span className="fw-semibold">
                                {formatCurrency(
                                  searchResults.product?.lbr_amt ??
                                    searchResults.product?.LBR_amt,
                                )}
                              </span>
                            </div>
                            <div className="d-flex justify-content-between align-items-center">
                              <div className="d-flex align-items-center gap-1">
                                <span className="text-dark small">
                                  Other Amount
                                </span>
                                {searchResults.product_charges &&
                                  searchResults.product_charges.length > 0 && (
                                    <Button
                                      variant="link"
                                      size="sm"
                                      className="p-0 text-primary"
                                      onClick={() => setShowChargesModal(true)}
                                      style={{
                                        minWidth: "auto",
                                        lineHeight: 1,
                                      }}
                                    >
                                      <Icon
                                        icon="mdi:information-outline"
                                        width="16"
                                      />
                                    </Button>
                                  )}
                              </div>
                              <span className="fw-semibold">
                                {formatCurrency(
                                  searchResults.product?.oth_amt ??
                                    searchResults.product?.othamt,
                                )}
                              </span>
                            </div>
                          </div>
                        </div>
                        <div className="col-md-12 col-lg-4">
                          <div
                            className={`h-100 rounded-4 border p-3 text-dark ${cardClass}`}
                          >
                            <div className="d-flex gap-2 align-items-center mb-3">
                              <h6 className="mb-0 fw-semibold">Image</h6>
                              {searchResults.product?.image && (
                                <Button
                                  variant="outline-primary"
                                  size="sm"
                                  onClick={() => {
                                    setSelectedImage(
                                      getImageUrl(searchResults.product.image),
                                    );
                                    setShowImageModal(true);
                                  }}
                                >
                                  <Icon
                                    icon="solar:eye-bold"
                                    width="18"
                                    className="me-1"
                                  />
                                </Button>
                              )}
                            </div>
                            {!searchResults.product?.image && (
                              <p className="text-dark small mb-0 mt-2">
                                No image available
                              </p>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {activeTab === "supplier" && (
                    <div className="pt-4">
                      {searchResults.karigar ? (
                        <div className="row g-3">
                          <div className="col-md-6 col-lg-4">
                            <div
                              className={`rounded-4 border p-3 text-dark ${cardClass}`}
                            >
                              <div className="d-flex justify-content-between align-items-center mb-3">
                                <span className="text-dark small">
                                  Company Name
                                </span>
                                <span className="fw-semibold text-dark">
                                  {searchResults.karigar.company_name || "N/A"}
                                </span>
                              </div>
                              <div className="d-flex justify-content-between align-items-center mb-3">
                                <span className="text-dark small">
                                  Contact Number
                                </span>
                                <span className="fw-semibold text-dark">
                                  {searchResults.karigar.contact_number ||
                                    "N/A"}
                                </span>
                              </div>
                              <div className="d-flex justify-content-between align-items-center mb-3">
                                <span className="text-dark small">Address</span>
                                <span className="fw-semibold text-dark text-end">
                                  {searchResults.karigar.address || "N/A"}
                                </span>
                              </div>
                              <div className="d-flex justify-content-between align-items-center">
                                <span className="text-dark small">City</span>
                                <span className="fw-semibold text-dark">
                                  {searchResults.karigar.city || "N/A"}
                                </span>
                              </div>
                            </div>
                          </div>
                        </div>
                      ) : (
                        <p className="text-muted">
                          No supplier information available
                        </p>
                      )}
                    </div>
                  )}

                  {activeTab === "sale" && (
                    <div className="pt-4">
                      {searchResults.sales ||
                      searchResults.sale_details ||
                      trailCustomer ? (
                        <div className="row g-3">
                          <div className="col-md-6">
                            <h6 className="fw-semibold mb-3">
                              Sales Information
                            </h6>
                            {searchResults.sales ||
                            searchResults.sale_details ? (
                              <div
                                className={`rounded-4 border p-3 text-dark ${cardClass}`}
                              >
                                {trailSaleNumber && (
                                  <div className="d-flex justify-content-between align-items-center mb-3">
                                    <span className="text-dark small">
                                      Sale Number
                                    </span>
                                    <span className="fw-semibold text-dark">
                                      {trailSaleNumber}
                                    </span>
                                  </div>
                                )}
                                {searchResults.sales?.delivery_date && (
                                  <div className="d-flex justify-content-between align-items-center mb-3">
                                    <span className="text-dark small">
                                      Delivery Date
                                    </span>
                                    <span className="fw-semibold text-dark">
                                      {formatDate(
                                        searchResults.sales.delivery_date,
                                      )}
                                    </span>
                                  </div>
                                )}
                                {searchResults.sale_details && (
                                  <>
                                    <div className="d-flex justify-content-between align-items-center mb-2">
                                      <span className="text-dark small">
                                        Final Amount
                                      </span>
                                      <span className="fw-semibold text-dark">
                                        {formatCurrency(
                                          searchResults.sale_details
                                            .final_amount ??
                                            searchResults.sale_details
                                              .grand_total,
                                        )}
                                      </span>
                                    </div>
                                    <div className="d-flex justify-content-between align-items-center mb-2">
                                      <span className="text-dark small">
                                        Discount
                                      </span>
                                      <span className="fw-semibold text-dark">
                                        {searchResults.sale_details.discount ||
                                          "0.00"}
                                        %
                                      </span>
                                    </div>
                                    <div className="d-flex justify-content-between align-items-center mb-2">
                                      <span className="text-dark small">
                                        Discount Amount
                                      </span>
                                      <span className="fw-semibold text-dark">
                                        {formatCurrency(
                                          searchResults.sale_details
                                            .discount_amount,
                                        )}
                                      </span>
                                    </div>
                                    <div className="d-flex justify-content-between align-items-center mb-2">
                                      <span className="text-dark small">
                                        GST %
                                      </span>
                                      <span className="fw-semibold text-dark">
                                        {searchResults.sale_details
                                          .gst_percent || "0.00"}
                                        %
                                      </span>
                                    </div>
                                    <div className="d-flex justify-content-between align-items-center mb-2">
                                      <span className="text-dark small">
                                        GST Amount
                                      </span>
                                      <span className="fw-semibold text-dark">
                                        {formatCurrency(
                                          searchResults.sale_details.gst_amount,
                                        )}
                                      </span>
                                    </div>
                                    <div className="d-flex justify-content-between align-items-center mb-2 border-top pt-2">
                                      <span className="text-dark small">
                                        Grand Total
                                      </span>
                                      <span className="fw-bold fs-5 text-dark">
                                        {formatCurrency(
                                          searchResults.sale_details
                                            .grand_total,
                                        )}
                                      </span>
                                    </div>
                                    <div className="d-flex justify-content-between align-items-center mb-2">
                                      <span className="text-dark small">
                                        Remaining Amount
                                      </span>
                                      <span className="fw-bold text-dark">
                                        {formatCurrency(
                                          searchResults.sale_details
                                            .remaining_amount,
                                        )}
                                      </span>
                                    </div>
                                    {searchResults.sale_details
                                      .followup_date && (
                                      <div className="d-flex justify-content-between align-items-center">
                                        <span className="text-dark small">
                                          Follow-up Date
                                        </span>
                                        <span className="fw-semibold text-dark">
                                          {formatDate(
                                            searchResults.sale_details
                                              .followup_date,
                                          )}
                                        </span>
                                      </div>
                                    )}
                                  </>
                                )}
                              </div>
                            ) : (
                              <p className="text-muted">
                                No sale information available
                              </p>
                            )}
                          </div>

                          <div className="col-md-6">
                            <h6 className="fw-semibold mb-3">
                              Customer Information
                            </h6>
                            {trailCustomer ? (
                              <div
                                className={`rounded-4 border p-3 text-dark ${cardClass}`}
                              >
                                <div className="d-flex justify-content-between align-items-center mb-3">
                                  <span className="text-dark small">Name</span>
                                  <span className="fw-semibold text-dark">
                                    {trailCustomer.name}
                                  </span>
                                </div>
                                <div className="d-flex justify-content-between align-items-center mb-3">
                                  <span className="text-dark small">
                                    Mobile
                                  </span>
                                  <span className="fw-semibold text-dark">
                                    {trailCustomer.mobile}
                                  </span>
                                </div>
                                <div className="d-flex justify-content-between align-items-center mb-3">
                                  <span className="text-dark small">City</span>
                                  <span className="fw-semibold text-dark">
                                    {trailCustomer.city}
                                  </span>
                                </div>
                                <div className="d-flex justify-content-between align-items-center">
                                  <span className="text-dark small">State</span>
                                  <span className="fw-semibold text-dark">
                                    {trailCustomer.state}
                                  </span>
                                </div>
                              </div>
                            ) : (
                              <p className="text-muted">
                                No customer information available
                              </p>
                            )}
                          </div>
                        </div>
                      ) : (
                        <p className="text-muted">
                          No sale information available
                        </p>
                      )}
                    </div>
                  )}

                  {activeTab === "payments" && (
                    <div className="pt-4">
                      {searchResults.payments &&
                      searchResults.payments.length > 0 ? (
                        <div className="row g-3">
                          {searchResults.payments.map((payment, index) => (
                            <div key={index} className="col-md-6 col-lg-4">
                              <div
                                className={`rounded-4 border p-3 text-dark ${cardClass}`}
                              >
                                <div className="d-flex justify-content-between align-items-center mb-3">
                                  <span className="text-dark small">
                                    Payment Mode
                                  </span>
                                  <span className="fw-semibold text-dark">
                                    {payment.mode || payment.pay_mode || "N/A"}
                                  </span>
                                </div>
                                <div className="d-flex justify-content-between align-items-center mb-3">
                                  <span className="text-dark small">
                                    Bank Type
                                  </span>
                                  <span className="fw-semibold text-dark">
                                    {payment.bank_name != null &&
                                    payment.bank_name !== ""
                                      ? payment.bank_name
                                      : payment.bank_type != null &&
                                          payment.bank_type !== ""
                                        ? String(payment.bank_type)
                                        : "N/A"}
                                  </span>
                                </div>
                                <div className="d-flex justify-content-between align-items-center mb-3">
                                  <span className="text-dark small">
                                    Amount
                                  </span>
                                  <span className="fw-bold text-dark">
                                    {formatCurrency(
                                      payment.total_amount ??
                                        payment.aavak_amt ??
                                        payment.jaavak_amt ??
                                        payment.amount,
                                    )}
                                  </span>
                                </div>
                                <div className="d-flex justify-content-between align-items-center mb-3">
                                  <span className="text-dark small">Date</span>
                                  <span className="fw-semibold text-dark">
                                    {formatDate(
                                      payment.date || payment.created_at,
                                    )}
                                  </span>
                                </div>
                                {payment.remarks && (
                                  <div className="mt-2 pt-2 border-top">
                                    <span className="text-dark small d-block mb-1">
                                      Remarks
                                    </span>
                                    <span className="fw-semibold small text-dark">
                                      {payment.remarks}
                                    </span>
                                  </div>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p className="text-muted">No payment records found</p>
                      )}
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>

        {/* Product Charges Modal */}
        <Modal
          show={showChargesModal}
          onHide={() => setShowChargesModal(false)}
          centered
        >
          <Modal.Header closeButton>
            <Modal.Title>Product Charges Breakdown</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {searchResults?.product_charges &&
            searchResults.product_charges.length > 0 ? (
              <div className="table-responsive">
                <Table bordered size="sm" className={tableClass}>
                  <thead>
                    <tr>
                      <th>Charge Type</th>
                      <th className="text-center">Pcs</th>
                      <th className="text-end">Rate</th>
                      <th className="text-end">Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    {searchResults.product_charges.map((charge, index) => {
                      const value =
                        (parseFloat(charge.pcs) || 0) *
                        (parseFloat(charge.rate) || 0);
                      return (
                        <tr key={index}>
                          <td>{charge.charge_type || "N/A"}</td>
                          <td className="text-center">{charge.pcs || 0}</td>
                          <td className="text-end">
                            {formatCurrency(charge.rate)}
                          </td>
                          <td className="text-end fw-semibold">
                            {formatCurrency(value.toFixed(2))}
                          </td>
                        </tr>
                      );
                    })}
                    <tr className="table-secondary fw-bold">
                      <td colSpan="3" className="text-end">
                        Total
                      </td>
                      <td className="text-end">
                        {formatCurrency(
                          searchResults.product_charges
                            .reduce((sum, charge) => {
                              return (
                                sum +
                                (parseFloat(charge.pcs) || 0) *
                                  (parseFloat(charge.rate) || 0)
                              );
                            }, 0)
                            .toFixed(2),
                        )}
                      </td>
                    </tr>
                  </tbody>
                </Table>
                <div className="mt-2 text-muted small">
                  <Icon
                    icon="mdi:information-outline"
                    width="16"
                    className="me-1"
                  />
                  Formula: Pcs × Rate = Amount
                </div>
              </div>
            ) : (
              <p className="text-muted mb-0">No charges available</p>
            )}
          </Modal.Body>
        </Modal>

        {/* Image Preview Modal */}
        <Modal
          show={showImageModal}
          onHide={() => setShowImageModal(false)}
          size="lg"
          centered
        >
          <Modal.Header closeButton>
            <Modal.Title>Product Image</Modal.Title>
          </Modal.Header>
          <Modal.Body className="text-center">
            {selectedImage && (
              <img
                src={selectedImage}
                alt="Product"
                style={{
                  maxWidth: "100%",
                  maxHeight: "70vh",
                  objectFit: "contain",
                }}
              />
            )}
          </Modal.Body>
        </Modal>
      </div>
    </>
  );
}
export default HuidBarcodeSearch;
